import { Field, type FieldInputProps } from '@ark-ui/react/field';
import { css, cva } from '@repo/panda/css';
import type { SystemStyleObject } from '@repo/panda/types';
import React from 'react';
import { Icon, type IconBaseProps } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';
import { generateErrorId, generateUniqueId } from '../utils/accessibility';
import { Type } from './type';

type TextInputIconProps = Omit<IconBaseProps, 'icon'>;

export interface TextInputProps {
    id?: string;
    errorText?: string;
    placeholder: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    fullWidth?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'dark' | 'light' | 'white';
    borderRadius?: 'full' | 'md' | 'sm' | 'xs' | 'none';
    'aria-label'?: string;
    'aria-describedby'?: string;
    helperText?: string;
    leadingIcon?: MaterialSymbolName;
    trailingIcon?: MaterialSymbolName;
    leadingIconProps?: TextInputIconProps;
    trailingIconProps?: TextInputIconProps;
    label?: string;
    required?: boolean;
    className?: SystemStyleObject;
}

const containerStyles = cva({
    base: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: 'full',
        border: '[1px solid transparent]',
        transition: '[all 0.2s ease]',
        outline: 'none',
        cursor: 'text',
        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    variants: {
        variant: {
            dark: {
                backgroundColor: 'whiteAlpha.10',
                _hover: {
                    backgroundColor: 'whiteAlpha.20',
                },
                _focusWithin: {
                    border: '[1px solid]',
                    borderColor: 'whiteAlpha.40',
                    backgroundColor: 'whiteAlpha.20',
                },
            },
            light: {
                backgroundColor: 'blackAlpha.20',
                _hover: {
                    backgroundColor: 'blackAlpha.30',
                },
                _focusWithin: {
                    backgroundColor: 'blackAlpha.30',
                },
            },
            white: {
                backgroundColor: 'white',
                _hover: {
                    backgroundColor: 'whiteAlpha.90',
                },
                _focusWithin: {
                    border: '[1px solid]',
                    borderColor: 'blackAlpha.20',
                    backgroundColor: 'white',
                },
            },
        },
        size: {
            xs: {
                height: '[36px]',
            },
            sm: {
                height: '[40px]',
            },
            md: {
                height: '[48px]',
            },
            lg: {
                minHeight: '[52px]',
            },
            xl: {
                minHeight: '[56px]',
            },
        },
        borderRadius: {
            full: { borderRadius: 'full' },
            md: { borderRadius: '12' },
            sm: { borderRadius: '8' },
            xs: { borderRadius: '4' },
            none: { borderRadius: '[0px]' },
        },
    },
    defaultVariants: {
        variant: 'dark',
        size: 'md',
        borderRadius: 'md',
    },
});

const inputStyles = cva({
    base: {
        width: 'full',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '[inherit]',
        outline: 'none',
        color: '[inherit]',
        fontSize: '[inherit]',
        position: 'relative',
        cursor: 'text',
        _disabled: {
            cursor: 'not-allowed',
        },
        overflow: 'hidden',
        // Prevent browser autofill from applying solid background or text color
        _autofill: {
            transition: '[background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s]',
            WebkitTextFillColor: '[inherit]',
        },
    },
    variants: {
        variant: {
            dark: {
                color: 'whiteAlpha.80',
                _placeholder: {
                    color: 'whiteAlpha.30',
                },
            },
            light: {
                color: 'wemod.black.80',
                _placeholder: {
                    color: 'wemod.black.50',
                },
            },
            white: {
                color: 'black',
                _placeholder: {
                    color: 'blackAlpha.60',
                },
            },
        },
        size: {
            xs: {
                padding: '[4px 8px]',
                fontSize: 'sm',
            },
            sm: {
                padding: '[8px 12px]',
                fontSize: 'md',
            },
            md: {
                padding: '[12px 16px]',
                fontSize: 'lg',
            },
            lg: {
                padding: '[16px 20px]',
                fontSize: 'xl',
            },
            xl: {
                padding: '[20px 24px]',
                fontSize: '2xl',
            },
        },
        hasLeadingIcon: {
            true: {},
            false: {},
        },
        hasTrailingIcon: {
            true: {},
            false: {},
        },
    },
    defaultVariants: {
        variant: 'dark',
        size: 'md',
        hasLeadingIcon: false,
        hasTrailingIcon: false,
    },
    compoundVariants: [
        // Adjust padding when icons are present
        { hasLeadingIcon: true, size: 'xs', css: { paddingLeft: '36' } },
        { hasLeadingIcon: true, size: 'sm', css: { paddingLeft: '40' } },
        { hasLeadingIcon: true, size: 'md', css: { paddingLeft: '[44px]' } },
        { hasLeadingIcon: true, size: 'lg', css: { paddingLeft: '48' } },
        { hasLeadingIcon: true, size: 'xl', css: { paddingLeft: '[52px]' } },
        { hasTrailingIcon: true, size: 'sm', css: { paddingRight: '40' } },
        { hasTrailingIcon: true, size: 'md', css: { paddingRight: '[44px]' } },
        { hasTrailingIcon: true, size: 'lg', css: { paddingRight: '48' } },
        { hasTrailingIcon: true, size: 'xl', css: { paddingRight: '[52px]' } },
    ],
});

const iconStyles = cva({
    base: {
        position: 'absolute',
        top: '[50%]',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
    },
    variants: {
        variant: {
            dark: {
                color: 'whiteAlpha.60',
            },
            light: {
                color: 'wemod.black.60',
            },
            white: {
                color: 'blackAlpha.70',
            },
        },
        position: {
            leading: {
                left: '16',
            },
            trailing: {
                right: '16',
            },
        },
        size: {
            // Below the icon scale; the other steps come from the icon's own `size`.
            xs: { fontSize: '[12px]' },
            sm: {},
            md: {},
            lg: {},
            xl: {},
        },
    },
    defaultVariants: {
        variant: 'dark',
        position: 'leading',
        size: 'md',
    },
});

const errorStyles = cva({
    base: {
        fontSize: 'sm',
        marginTop: '4',
    },
    variants: {
        variant: {
            dark: { color: 'error' },
            light: { color: 'red.600' },
            white: { color: 'red.600' },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const helperTextStyles = cva({
    base: {
        fontSize: 'sm',
        marginTop: '4',
    },
    variants: {
        variant: {
            dark: { color: 'wemod.black.50' },
            light: { color: 'wemod.black.60' },
            white: { color: 'wemod.black.60' },
        },
    },
    defaultVariants: {
        variant: 'dark',
    },
});

const labelStyles = cva({
    base: {},
    variants: {
        required: {
            true: {
                _after: {
                    content: '" *"',
                    color: 'error',
                },
            },
        },
    },
    defaultVariants: {
        required: false,
    },
});

export const TextInput = React.forwardRef<
    HTMLInputElement,
    TextInputProps & Omit<FieldInputProps, 'size' | 'className'>
>(
    (
        {
            errorText,
            helperText,
            placeholder,
            value,
            onChange,
            disabled = false,
            fullWidth = false,
            size = 'md' as const,
            variant = 'dark' as const,
            borderRadius = 'md' as const,
            leadingIcon,
            trailingIcon,
            leadingIconProps,
            trailingIconProps,
            label,
            required = false,
            className,
            'aria-label': ariaLabel,
            'aria-describedby': ariaDescribedBy,
            ...rest
        },
        ref,
    ) => {
        const hasError = Boolean(errorText);
        const hasHelperText = Boolean(helperText);

        // Generate IDs for accessibility
        const errorId = generateErrorId('text-input', hasError, ariaDescribedBy);

        const rootStyles = css({
            width: fullWidth ? 'full' : 'fit',
        });

        // Generate unique ID for the input if label is provided
        const inputId = label ? generateUniqueId('text-input') : rest.id;

        return (
            <div className={rootStyles}>
                <Field.Root
                    className={css(
                        {
                            borderRadius: borderRadius,
                        },
                        className,
                    )}
                >
                    {label && (
                        <Field.Label asChild htmlFor={inputId} className={labelStyles({ required })}>
                            <Type variant="inputHint" className={css({ marginBottom: '6' })}>
                                {label}
                            </Type>
                        </Field.Label>
                    )}

                    <div className={containerStyles({ variant, size, borderRadius })}>
                        {leadingIcon && (
                            <Icon
                                icon={leadingIcon}
                                size={size === 'xs' ? undefined : size}
                                className={iconStyles({ variant, position: 'leading', size })}
                                {...leadingIconProps}
                                decorative
                            />
                        )}

                        <Field.Input
                            ref={ref}
                            id={inputId}
                            className={inputStyles({
                                variant,
                                size,
                                hasLeadingIcon: Boolean(leadingIcon),
                                hasTrailingIcon: Boolean(trailingIcon),
                            })}
                            placeholder={placeholder}
                            value={value}
                            onChange={onChange}
                            disabled={disabled}
                            required={required}
                            aria-label={ariaLabel}
                            aria-describedby={hasError ? errorId : ariaDescribedBy}
                            aria-invalid={hasError}
                            {...rest}
                        />

                        {trailingIcon && (
                            <Icon
                                icon={trailingIcon}
                                size={size === 'xs' ? undefined : size}
                                className={iconStyles({ variant, position: 'trailing', size })}
                                {...trailingIconProps}
                                decorative
                            />
                        )}
                    </div>
                </Field.Root>

                {hasHelperText && <div className={helperTextStyles({ variant })}>{helperText}</div>}

                {hasError && (
                    <div className={errorStyles({ variant })} id={errorId} role="alert" aria-live="polite">
                        {errorText}
                    </div>
                )}
            </div>
        );
    },
);

TextInput.displayName = 'TextInput';
